import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import { Alert, Box, Button, Card, CardContent, Chip, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { EmptyState, ErrorState } from '@/shared/ui';

import { useBudgetsForIds, useCampaigns, usePromotions } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import type { BudgetResponse, BudgetScope } from '../../model/types';
import { BudgetBurnBar } from './BudgetBurnBar';
import { BudgetFormDrawer } from './BudgetFormDrawer';

export interface BudgetsPanelProps {
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * "Budgets" — budgets with burn (plan-011 §8.6).
 *
 * Contract-driven adaptation: the BFF has no `list budgets` endpoint, and `PromotionResponse`
 * carries no back-reference to a budget (only `CampaignResponse.budgetId` does — see
 * `api/budgetsApi.ts`). This panel therefore discovers CAMPAIGN-scoped budgets by scanning the
 * campaigns list for a non-null `budgetId`; a PROMOTION-scoped budget only becomes visible once
 * created in this session (tracked locally) since there is no way to re-discover it after a reload
 * without already knowing its id. Editing/deleting a budget is not offered (no PUT/DELETE exists).
 */
export function BudgetsPanel({ onResult }: BudgetsPanelProps) {
  const { t } = useTranslation('promotions');
  const { canManageBudgets } = usePromoPermissions();
  const [scopeFilter, setScopeFilter] = useState<BudgetScope | ''>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sessionBudgetIds, setSessionBudgetIds] = useState<string[]>([]);

  const campaigns = useCampaigns({ size: 200 });
  const promotions = usePromotions({ size: 200 });

  const campaignBudgetIds = useMemo(
    () => (campaigns.data?.content ?? []).filter((c) => c.budgetId).map((c) => c.budgetId as string),
    [campaigns.data],
  );
  const allBudgetIds = useMemo(
    () => Array.from(new Set([...campaignBudgetIds, ...sessionBudgetIds])),
    [campaignBudgetIds, sessionBudgetIds],
  );

  const budgetQueries = useBudgetsForIds(allBudgetIds);
  const budgets = budgetQueries.map((q) => q.data).filter((b): b is BudgetResponse => Boolean(b));
  const anyLoading = budgetQueries.some((q) => q.isLoading) || campaigns.isLoading;
  const anyError = campaigns.isError;

  const campaignFor = (budgetId: string) => campaigns.data?.content.find((c) => c.budgetId === budgetId);
  const promotionFor = (promotionId: string | null | undefined) =>
    promotionId ? promotions.data?.content.find((p) => p.id === promotionId) : undefined;

  const filteredBudgets = budgets.filter((b) => !scopeFilter || b.scope === scopeFilter);

  if (anyError) {
    return (
      <ErrorState
        title={t('budgets.loadError.title')}
        message={campaigns.error?.message ?? t('common:error.unknown')}
        onRetry={() => void campaigns.refetch()}
      />
    );
  }

  return (
    <>
      {!canManageBudgets ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('budgets.readOnlyAlert')}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <TextField
          select
          size="small"
          label={t('budgets.scopeFilterLabel')}
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value as BudgetScope | '')}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('budgets.allScopes')}</MenuItem>
          <MenuItem value="CAMPAIGN">{t('budgetFormDrawer.scope.CAMPAIGN')}</MenuItem>
          <MenuItem value="PROMOTION">{t('budgetFormDrawer.scope.PROMOTION')}</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        {canManageBudgets ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDrawerOpen(true)}>
            {t('budgets.newBudget')}
          </Button>
        ) : null}
      </Stack>

      {!anyLoading && filteredBudgets.length === 0 ? (
        <EmptyState
          icon={<SavingsOutlinedIcon sx={{ fontSize: 48 }} />}
          title={t('budgets.empty.title')}
          description={t('budgets.empty.description')}
          action={
            canManageBudgets ? (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDrawerOpen(true)}>
                {t('budgets.newBudget')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Grid container spacing={2}>
          {filteredBudgets.map((budget) => {
            const campaign = budget.scope === 'CAMPAIGN' ? campaignFor(budget.id) : undefined;
            const promotion = budget.scope === 'PROMOTION' ? promotionFor(budget.promotionId) : undefined;
            return (
              <Grid key={budget.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" variant="outlined" label={t(`budgetFormDrawer.scope.${budget.scope}`)} />
                      {campaign ? (
                        <Button size="small" component={RouterLink} to={`/promo/campaigns/${campaign.id}`}>
                          {campaign.name}
                        </Button>
                      ) : promotion ? (
                        <Button size="small" component={RouterLink} to={`/promo/promotions/${promotion.id}`}>
                          {promotion.name}
                        </Button>
                      ) : null}
                    </Stack>
                    <BudgetBurnBar budget={budget} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      {t('budgets.autoPauseCaption', {
                        state: budget.onExhaust === 'AUTO_PAUSE' ? t('common:status.success') : t('common:status.error'),
                        threshold: budget.alertThresholdPct ?? 80,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <BudgetFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(created) => {
          // No list endpoint to invalidate-and-refetch against — track the new id directly so a
          // PROMOTION-scoped budget (which campaigns can't reveal) stays visible this session.
          setSessionBudgetIds((prev) => (prev.includes(created.id) ? prev : [...prev, created.id]));
          void campaigns.refetch();
        }}
        onResult={onResult}
      />
    </>
  );
}
